"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _memoizeOne = _interopRequireDefault(require("memoize-one"));
var _TruncatedText = _interopRequireDefault(require("../style/TruncatedText.css"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2018 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

/* eslint semi:0 */

const midPos = text => {
  const midIndex = text.length / 2;
  // Check to see if the middle pos is part of a surrogate pair
  // If it is, make sure we don't split the pair.
  if (midIndex >= 1 && text.codePointAt(midIndex - 1) > 0x10000) {
    return midIndex - 1;
  }
  return midIndex;
};
const isBidiOrComplex = text => text.match(/[\u0590-\u10a0\u1780-\u1e00]/u);

// Polyfill for IE
const trimRight = String.prototype.trimRight || new Function('return this.replace(/\\s+$/g, \'\')'); // eslint-disable-line no-new-func

/**
 * A component which will render a string with an ellipsis either
 * in the middle of a string or at the end
 */
class TruncatedText extends _react.Component {
  constructor(...args) {
    super(...args);
    this.splitText = (0, _memoizeOne.default)((text, emphasis) => {
      // use canonical composite characters so that individual decomposed characters
      // are not rendered in pieces
      const textNormalized = text.normalize();
      const mid = midPos(textNormalized);
      const leftSide = textNormalized.substr(0, mid);
      const rightSide = textNormalized.substr(mid);

      // DCES-4231844: If left side has trailing whitespace, move it to the right
      // so that it will be wrapped in a tag and preserved
      const whitespace = trimRight.call(leftSide) !== leftSide;
      let left = whitespace ? leftSide.slice(0, -1) : leftSide;

      // Break up right half into individual tags which will be reversed and laid out right to left
      const stringToTags = str => Array.from(str).map((ch, i) => /*#__PURE__*/_react.default.createElement("span", {
        className: _TruncatedText.default.right,
        key: str.concat(i)
      }, ch));
      let right = stringToTags(whitespace ? ` ${rightSide}` : rightSide);
      let textWithEmphasis = textNormalized;

      // Option to render a substring in bold
      if (emphasis) {
        const emphasisNormalized = emphasis.normalize();
        const start = textNormalized.indexOf(emphasisNormalized);
        if (start !== -1) {
          const end = start + emphasisNormalized.length;
          if (end > left.length) {
            // if the emphasis string is present in the right half,
            // add a bold class to tags containing the individual characters
            const makeBold = ({
              type: Type,
              props: {
                className,
                children
              },
              key
            }) => /*#__PURE__*/_react.default.createElement(Type, {
              className: `${className} ${_TruncatedText.default.bold}`,
              key: key
            }, children);
            const boldStart = start - left.length;
            const boldEnd = end - left.length;
            right = right.map((tag, i) => i >= boldStart && i < boldEnd ? makeBold(tag) : tag);
          }
          if (start < left.length) {
            // if the emphasis string is present in the left half,
            // construct a Fragment and wrap the emphasis string with a bold tag
            left = /*#__PURE__*/_react.default.createElement(_react.Fragment, null, left.slice(0, start), /*#__PURE__*/_react.default.createElement("b", null, left.slice(start, Math.min(end, left.length))), left.slice(end, Math.max(left.length, end)));
          }
          textWithEmphasis = /*#__PURE__*/_react.default.createElement(_react.Fragment, null, textNormalized.slice(0, start), /*#__PURE__*/_react.default.createElement("mark", {
            className: _TruncatedText.default.bold
          }, textNormalized.slice(start, end)), textNormalized.slice(end, textNormalized.length));
        }
      }
      right = right.reverse();
      return {
        left,
        right,
        textWithEmphasis
      };
    });
  }
  addClassName(clazz) {
    const {
      className
    } = this.props;
    const classList = className ? [className] : [];
    classList.push(clazz);
    return classList.join(' ');
  }
  render() {
    const {
      dir,
      emphasis,
      height,
      placement,
      text
    } = this.props;

    // Calculate the left and right halves of the text. If these arguments haven't changed
    // since the last render, `memoize-one` will reuse the last return value.
    const {
      left,
      right,
      textWithEmphasis
    } = this.splitText(text, emphasis);
    if (placement === 'middle' && !isBidiOrComplex(text)) {
      return /*#__PURE__*/_react.default.createElement("div", {
        role: "presentation",
        className: this.addClassName(_TruncatedText.default.wrapper),
        style: {
          lineHeight: `${height}px`
        },
        title: text
      }, /*#__PURE__*/_react.default.createElement("div", {
        "aria-hidden": "true",
        className: _TruncatedText.default.left
      }, left), /*#__PURE__*/_react.default.createElement("div", {
        className: _TruncatedText.default.visuallyHidden
      }, textWithEmphasis), /*#__PURE__*/_react.default.createElement("div", {
        "aria-hidden": "true",
        className: _TruncatedText.default.wrapRight
      }, right));
    }
    return /*#__PURE__*/_react.default.createElement("div", {
      role: "presentation",
      className: this.addClassName(_TruncatedText.default.truncate),
      style: {
        lineHeight: `${height}px`
      },
      title: text,
      dir: dir
    }, emphasis !== TruncatedText.defaultProps.emphasis ? textWithEmphasis : text);
  }
}
exports.default = TruncatedText;
TruncatedText.propTypes = {
  className: _propTypes.default.string,
  dir: _propTypes.default.oneOf(['ltr', 'rtl', 'auto']),
  emphasis: _propTypes.default.string,
  height: _propTypes.default.number,
  placement: _propTypes.default.oneOf(['middle', 'end']),
  text: _propTypes.default.string
};
TruncatedText.defaultProps = {
  className: '',
  dir: undefined,
  emphasis: '',
  height: 32,
  placement: 'middle',
  text: ''
};